import { z } from "zod";

/** Mirrors backend/v2/tenants/onboarding_config.py */

export const ONBOARDING_MODE_LITE = "LITE";
export const ONBOARDING_MODE_FULL = "FULL";

export const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];

export const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

export const SUGGESTION_FIELDS = [
  { key: "welcome_message", label: "Welcome message", multiline: true },
  { key: "primary_color", label: "Primary color", color: true },
  { key: "secondary_color", label: "Secondary color", color: true },
  { key: "onboarding_mode", label: "Onboarding mode", mode: true },
  { key: "industry", label: "Industry" },
  { key: "company_size", label: "Company size" },
  { key: "timezone", label: "Timezone" },
];

export const SUGGESTION_TO_FORM = {
  welcome_message: "welcomeMessage",
  primary_color: "primaryColor",
  secondary_color: "secondaryColor",
  onboarding_mode: "onboardingMode",
  industry: "industry",
  company_size: "companySize",
  timezone: "timezone",
};

export const FORM_TO_SUGGESTION = Object.fromEntries(
  Object.entries(SUGGESTION_TO_FORM).map(([suggestionKey, formKey]) => [formKey, suggestionKey])
);

const HEX = /^#[0-9a-f]{6}$/;

export function expandHex(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  const match = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(withHash);
  if (!match) return withHash.toLowerCase();
  let digits = match[1].toLowerCase();
  if (digits.length === 3) digits = digits.split("").map((ch) => ch + ch).join("");
  return `#${digits}`;
}

const hexField = z
  .string()
  .transform((value) => expandHex(value))
  .refine((value) => value === "" || HEX.test(value), {
    message: "Use a hex color like #004f73",
  });

const timezoneField = z
  .string()
  .trim()
  .max(64)
  .refine((value) => value === "" || COMMON_TIMEZONES.includes(value), {
    message: "Choose a timezone from the list",
  });

export const onboardingFormSchema = z.object({
  logoUrl: z
    .string()
    .trim()
    .max(7000000, "Logo is too large")
    .refine(
      (value) =>
        value === "" ||
        /^https?:\/\//i.test(value) ||
        value.startsWith("/") ||
        value.startsWith("data:image/"),
      "Logo must be an http(s) URL, a site path, or an uploaded image"
    ),
  primaryColor: hexField,
  secondaryColor: hexField,
  welcomeMessage: z.string().trim().max(2000, "Welcome message must be at most 2000 characters"),
  onboardingMode: z.enum([ONBOARDING_MODE_LITE, ONBOARDING_MODE_FULL], {
    errorMap: () => ({ message: "Onboarding mode must be LITE or FULL" }),
  }),
  industry: z.string().trim().max(80, "Industry must be at most 80 characters"),
  companySize: z.enum(["", ...COMPANY_SIZES], {
    errorMap: () => ({ message: "Choose a company size from the list" }),
  }),
  timezone: timezoneField,
  adminContactEmail: z
    .string()
    .trim()
    .max(120)
    .refine((value) => value === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), {
      message: "Enter a valid admin contact email",
    }),
});

export const onboardingSuggestSchema = z.object({
  industry: z.string().trim().min(1, "Industry is required to generate suggestions").max(80),
  company_size: z.enum(["", ...COMPANY_SIZES]),
  timezone: timezoneField,
  onboarding_mode: z.enum([ONBOARDING_MODE_LITE, ONBOARDING_MODE_FULL]),
  product_display_name: z.string().trim().max(120),
  primary_color: hexField,
  brief: z.string().trim().max(500, "Context must be at most 500 characters"),
});

export function emptyOnboardingForm() {
  return {
    logoUrl: "",
    primaryColor: "",
    secondaryColor: "",
    welcomeMessage: "",
    onboardingMode: ONBOARDING_MODE_FULL,
    industry: "",
    companySize: "",
    timezone: "",
    adminContactEmail: "",
  };
}

export function formFromWorkspace(workspace) {
  const base = emptyOnboardingForm();
  if (!workspace) return base;
  const config = workspace.onboarding_config || {};
  const org = config.organization || {};
  const mode = String(config.onboarding_mode || "").toUpperCase();
  const size = String(org.company_size || "");
  const timezone = String(org.timezone || "");
  return {
    ...base,
    logoUrl: workspace.logo_url || "",
    primaryColor: workspace.primary_color || "",
    secondaryColor: workspace.secondary_color || "",
    welcomeMessage: workspace.welcome_message || "",
    onboardingMode: mode === ONBOARDING_MODE_LITE ? ONBOARDING_MODE_LITE : ONBOARDING_MODE_FULL,
    industry: org.industry || "",
    companySize: COMPANY_SIZES.includes(size) ? size : "",
    timezone: COMMON_TIMEZONES.includes(timezone) ? timezone : "",
    adminContactEmail: org.admin_contact_email || "",
  };
}

export function snapshotForm(form) {
  return JSON.stringify(form);
}

export function toOnboardingApiPayload(data) {
  return {
    logo_url: data.logoUrl,
    primary_color: data.primaryColor,
    secondary_color: data.secondaryColor,
    welcome_message: data.welcomeMessage,
    onboarding_mode: data.onboardingMode,
    organization: {
      industry: data.industry,
      company_size: data.companySize,
      timezone: data.timezone,
      admin_contact_email: data.adminContactEmail,
    },
  };
}

export function toSuggestInput(form, brief, productName) {
  return {
    industry: String(form.industry || "").trim(),
    company_size: form.companySize || "",
    timezone: form.timezone || "",
    onboarding_mode: form.onboardingMode,
    product_display_name: String(productName || "").trim(),
    primary_color: form.primaryColor || "",
    brief: String(brief || "").trim(),
  };
}

export function zodFieldErrors(error) {
  const errors = {};
  for (const issue of error?.issues || []) {
    const key = issue.path?.[0];
    if (key && !errors[key]) errors[key] = issue.message;
  }
  return errors;
}

const SUGGEST_ERROR_TO_FORM = {
  industry: "industry",
  company_size: "companySize",
  timezone: "timezone",
  onboarding_mode: "onboardingMode",
  primary_color: "primaryColor",
  brief: "brief",
};

export function suggestErrorsToForm(error) {
  const raw = zodFieldErrors(error);
  const errors = {};
  for (const [key, message] of Object.entries(raw)) {
    errors[SUGGEST_ERROR_TO_FORM[key] || key] = message;
  }
  return errors;
}

export function readFormField(form, suggestionKey) {
  const formKey = SUGGESTION_TO_FORM[suggestionKey];
  return formKey ? form?.[formKey] ?? "" : "";
}

export function writeFormField(form, suggestionKey, value) {
  const formKey = SUGGESTION_TO_FORM[suggestionKey];
  if (!formKey) return form;
  return { ...form, [formKey]: value };
}

export function suggestionStateFromApi(data, form) {
  const org = data?.organization || {};
  const candidates = {
    welcome_message: data?.welcome_message,
    primary_color: data?.primary_color,
    secondary_color: data?.secondary_color,
    onboarding_mode: data?.onboarding_mode,
    industry: org.industry,
    company_size: org.company_size,
    timezone: org.timezone,
  };
  const fields = {};
  for (const [key, value] of Object.entries(candidates)) {
    const text = String(value ?? "").trim();
    if (!text) continue;
    if (text === String(readFormField(form, key) ?? "").trim()) continue;
    fields[key] = { value: text, decision: "pending", previous: null };
  }
  return { source: data?.source || "template", fields };
}
